import {Marketer} from '../models/marketer.model';

export interface SelectMarketerRequest {
  marketerId: string;
  marketers: Marketer[];
}

export interface SelectMarketerResponse {
  selectedMarketer: Marketer | undefined;
  success: boolean;
  message?: string;
}

export class SelectMarketerUseCase {
  execute (request: SelectMarketerRequest): SelectMarketerResponse {
    const {marketerId, marketers} = request;

    if (!marketerId) {
      return {
        selectedMarketer: undefined,
        success: true,
        message: 'Marketer selection cleared'
      };
    }

    // البحث عن المسوق في القائمة
    const selectedMarketer = marketers.find(marketer => marketer.id === marketerId);

    if (!selectedMarketer) {
      return {
        selectedMarketer: undefined,
        success: false,
        message: `Marketer with ID ${marketerId} not found`
      };
    }


    return {
      selectedMarketer,
      success: true,
      message: `Marketer ${selectedMarketer.displayName} selected successfully`
    };
  }

  selectMarketerByLocation (
    marketers: Marketer[],
    latitude: number,
    longitude: number,
    maxDistance: number = 0.1
  ): SelectMarketerResponse {
    if (marketers.length === 0) {
      return {
        selectedMarketer: undefined,
        success: false,
        message: 'No marketers available'
      };
    }

    // حساب المسافات وإيجاد أقرب مسوق
    let closestMarketer: Marketer | undefined;
    let closestDistance = Infinity;

    for (const marketer of marketers) {
      const distance = this.calculateDistance(
        {lat: latitude, lng: longitude},
        {lat: marketer.latitude, lng: marketer.longitude}
      );

      if (distance < closestDistance && distance <= maxDistance) {
        closestDistance = distance;
        closestMarketer = marketer;
      }
    }

    if (!closestMarketer) {
      return {
        selectedMarketer: undefined,
        success: false,
        message: `No marketer found within ${maxDistance}km of the specified location`
      };
    }


    return {
      selectedMarketer: closestMarketer,
      success: true,
      message: `Selected closest marketer ${closestMarketer.displayName}`
    };
  }

  private calculateDistance (
    point1: { lat: number; lng: number },
    point2: { lat: number; lng: number }
  ): number {
    const R = 6371; // نصف قطر الأرض بالكيلومتر
    const dLat = this.toRadians(point2.lat - point1.lat);
    const dLng = this.toRadians(point2.lng - point1.lng);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRadians(point1.lat)) *
        Math.cos(this.toRadians(point2.lat)) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private toRadians (degrees: number): number {
    return degrees * (Math.PI / 180);
  }
}
